import { useState, useEffect } from 'react';
import { Trophy, Award, Star, Linkedin, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import Confetti from './Confetti';

type TierCompleteProps = {
  tier: number;
  score: number;
  totalQuestions: number;
  prizeMessage: string;
  qrCode: string;
  onContinue?: () => void;
  showContinue: boolean;
  aiIqScore?: string;
  intelligenceTier?: string;
  interpretation?: string;
  messages: {
    tier1Title: string;
    tier2Title: string;
    tier3Title: string;
    scoreText: string;
    tier1Continue: string;
    tier2Continue: string;
    tier3Complete: string;
    continueButton: string;
  };
  socialShare?: {
    heading: string;
    text: string;
    url: string;
  };
  quizId: string;
};

export default function TierComplete({
  tier,
  score,
  totalQuestions,
  prizeMessage,
  qrCode,
  onContinue,
  showContinue,
  aiIqScore,
  intelligenceTier,
  interpretation,
  messages,
  socialShare,
  quizId,
}: TierCompleteProps) {
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (aiIqScore && parseInt(aiIqScore) >= 136) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [aiIqScore]);
  const getTierIcon = () => {
    switch (tier) {
      case 1:
        return <Trophy className="w-16 h-16 text-brand-green" />;
      case 2:
        return <Award className="w-16 h-16 text-brand-green" />;
      case 3:
        return <Star className="w-16 h-16 text-brand-green" />;
      default:
        return <Trophy className="w-16 h-16 text-brand-green" />;
    }
  };

  const getTierTitle = () => {
    switch (tier) {
      case 1:
        return messages.tier1Title;
      case 2:
        return messages.tier2Title;
      case 3:
        return messages.tier3Title;
      default:
        return messages.tier1Title;
    }
  };

  const getShareText = (socialType) => {
    if (!socialShare || !aiIqScore || !intelligenceTier || !interpretation) return '';
    return socialShare[socialType]
      .replace('{iq}', aiIqScore)
      .replace('{iq_name}', intelligenceTier)
      .replace('{iq_desc}', interpretation);
  };

  const handleLinkedInShare = async () => {
    const text = getShareText('linkedIn');
    try {
      await navigator.clipboard.writeText(text);
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 5000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleTwitterShare = () => {
    const text = getShareText('x');
    const url = socialShare?.url || window.location.href;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=600');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-light/30 to-brand-light/60 flex items-center justify-center p-4">
      {showConfetti && <Confetti />}
      {showCopyToast && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-brand-green text-brand-dark px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 animate-[slideDown_0.3s_ease-out]">
          <Check className="w-5 h-5" />
          <span className="font-semibold">Post copied! Open LinkedIn and paste it into a new post</span>
        </div>
      )}
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-brand-light/50 rounded-full mb-4">
            {getTierIcon()}
          </div>
          <h1 className="text-3xl font-bold text-brand-dark mb-2 font-heading">{getTierTitle()}</h1>
          <p className="text-xl text-brand-dark/70 mb-4">
            {messages.scoreText
              .replace('{score}', score.toString())
              .replace('{total}', totalQuestions.toString())}
          </p>
          {aiIqScore && intelligenceTier && (
            <div className="mt-6 space-y-3">
              <div className="bg-brand-green/20 rounded-xl p-4 border-2 border-brand-green">
                <div className="text-sm text-brand-dark/70 font-medium mb-1">
                  Your Final AI IQ Score
                </div>
                <div className="text-4xl font-bold font-heading text-brand-dark mb-2">{aiIqScore}</div>
                <div className="text-lg font-semibold text-brand-green">{intelligenceTier}</div>
              </div>
                <div className="bg-brand-light/50 rounded-xl p-4 border border-brand-green/30">
                  <p className="text-sm text-brand-dark/80 italic">{interpretation}</p>
                </div>
            </div>
          )}
        </div>

        <div className="bg-brand-light/30 border-2 border-brand-green rounded-xl p-6 mb-6">
          <div className="flex justify-center mb-4">
            <div className="bg-white p-4 rounded-lg">
              <QRCodeSVG value={qrCode} size={200} />
            </div>
          </div>
          <p className="text-center text-brand-dark font-medium">{prizeMessage}</p>
        </div>

        {showContinue && (
          <div className="space-y-3">
            <p className="text-center text-brand-dark/70 text-sm">
              {tier === 1 && messages.tier1Continue}
              {tier === 2 && messages.tier2Continue}
            </p>
            <button
              onClick={onContinue}
              className="w-full bg-brand-green text-brand-dark py-3 rounded-lg font-semibold hover:opacity-90 transition transform active:scale-95"
            >
              {messages.continueButton}
            </button>
          </div>
        )}

        
        {socialShare && aiIqScore && intelligenceTier && interpretation && (
          <div className="bg-gradient-to-r from-brand-light/50 to-brand-green/20 border-2 border-brand-green/40 rounded-xl p-6 mb-6">
            <h3 className="text-center text-lg font-semibold text-brand-dark mb-4">
              {socialShare.heading}
            </h3>
            <div className="flex justify-center gap-4">
              <button
                onClick={handleLinkedInShare}
                className="flex items-center justify-center w-14 h-14 bg-[#0A66C2] hover:bg-[#004182] rounded-lg transition transform active:scale-95 shadow-md"
                aria-label="Share on LinkedIn"
              >
                <Linkedin className="w-7 h-7 text-white" />
              </button>
              <button
                onClick={handleTwitterShare}
                className="flex items-center justify-center w-14 h-14 bg-black hover:bg-gray-800 rounded-lg transition transform active:scale-95 shadow-md"
                aria-label="Share on X/Twitter"
              >
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </button>
            </div>
          </div>
        )}

          <div className="mt-4 space-y-3">
            <p className="text-center text-brand-dark/70 text-sm">
              {messages.tier3Complete}
            </p>
            <a
              href={`/${quizId}/leaderboard`}
              className="block w-full bg-brand-green text-brand-dark py-3 rounded-lg font-semibold hover:opacity-90 transition transform active:scale-95 text-center"
            >
              View Leaderboard
            </a>
          </div>
      </div>
    </div>
  );
}
